import React, { useState, useEffect, memo } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import "./CustomProperty.css";
import { convertSelectOptionData, getObjectLogicalName } from "./utility";
import { useDispatch, useSelector } from "react-redux";
import { getNextKey, capitalizeFirstLetter } from "app/utils";
import { useTranslation } from "react-i18next";
import { get } from "lodash";
import { showErrors } from "store/exception/actions";
import { getCommonUserDetail } from "../../../store/selectors";
import {
  getCustomProperties,
  getCustomPropertiesById,
  getCustomPropertiesCodes,
  getCustomPropertyListValuesDependencies,
  postCustomPropertiesListValues,
} from "../../../services/ApiService";
import {
  CPCode,
  CustomPropertyProps,
  ICustomField,
  ICustomProperties,
  IDependencyField,
} from "./types";
import { convertSelectDefaultValue, getObjectLogicalId } from "./utility";
import CustomField from "./components";
import { SelectOptionType } from "../select/Select";
import qs from "qs";
import { loaderLock, loaderUnlock } from "store/common/actions";

const CustomProperty: React.FC<CustomPropertyProps> = (props) => {
  let { t, i18n } = useTranslation();

  const methods = useFormContext<{ customFields: ICustomField[] }>();
  const {
    control,
    reset,
    formState: { errors },
  } = methods;
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "customFields",
  });

  const [objectLogicalNameId, setObjectLogicalNameId] = useState(0);
  const [codes, setCodes] = useState<CPCode[]>([]);
  const [isLoading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const userDetail = useSelector(getCommonUserDetail);
  const occupationsId = props.occupationsId
    ? props.occupationsId
    : userDetail.occupations[0]
      ? userDetail.occupations[0].occupationId
      : undefined;
  const companyId = props.companyId
    ? props.companyId
    : userDetail.occupations[0]
      ? userDetail.occupations[0].company.id
      : undefined;

  const customPropertyFieldMapper = (rawCPData: ICustomProperties[]) => {
    return rawCPData.map((item, index) => {
      return {
        ...item,
        parentKey: item?.parentKey ? String(item?.parentKey) : "",
        customPropertyId: item.id,
        refId: getNextKey("CPID"),
        key: !item.isAllocationAvailable
          ? String(getNextKey("CPID")) || ""
          : String(`${getNextKey("CPID")}-${index}`) || "",
      };
    });
  };

  const clearDependencies = (parentKey: string) => {
    const collectKeysToRemove = (
      key: string,
      allFields: ICustomField[]
    ): string[] => {
      const directChildren = allFields.filter((f) => f.parentKey === key);
      return directChildren.reduce<string[]>((acc, child) => {
        return acc.concat(
          [child.key],
          collectKeysToRemove(child.key, allFields)
        );
      }, []);
    };

    const keysToRemove: string[] = collectKeysToRemove(parentKey, fields);

    for (let i = fields.length - 1; i >= 0; i--) {
      if (keysToRemove.includes(fields[i].key)) {
        remove(i);
      }
    }
  };

  const handleChangeAllocation = async (
    value: SelectOptionType[],
    idObject?: string,
    percentChanged?: boolean,
    deleteKey?: string,
    isAppend?: boolean,
    newSelectValue?: SelectOptionType
  ) => {
    const index = fields?.findIndex(
      (item) =>
        String(item?.customPropertyId) === String(value[0]?.customPropertyId)
    );
    update(index, {
      ...fields[index],
      value: value,
    });
    if (percentChanged) return;
    if (idObject) {
      for (let i = 0; i < value.length; i++) {
        await handleSelectValueChange(value[i], idObject);
      }
    } else {
      // allocation delete
      if (deleteKey) {
        clearDependencies(deleteKey);
        return;
        // allocation append
      } else if (!isAppend && newSelectValue) {
        await handleSelectValueChange(newSelectValue);
      }
    }
  };
  console.log("fields", fields);
  const handleSelectValueChange = async (
    value: SelectOptionType | SelectOptionType[],
    idObject?: string
  ) => {
    if (value && Array.isArray(value)) {
      dispatch(loaderUnlock());
      return;
    }

    const test = fields.find((e) => String(e?.key) === String(value?.key));

    if (!value || (test && !test.isVisible)) {
      dispatch(loaderUnlock());
      return;
    }

    if (test && !test.isSelect && !test.isAllocationAvailable) {
      update(fields.indexOf(test), {
        ...test,
        value: value.text,
      });
      dispatch(loaderUnlock());
      return;
    }
    dispatch(loaderLock());
    let newCP: ICustomProperties[] = [];
    clearDependencies(String(value.key));
    const dependenciesIds = await getCustomPropertyListValuesDependencies(
      {},
      `?CustomPropertyListValueIds=${value.value}`
    )
      .then((res) => {
        if (res?.headers?.success) {
          return res?.data;
        }
      })
      .then((res): [number[], IDependencyField[], number[]] => {
        const result: number[] = [];
        const propertyOnValueResult: number[] = [];
        const values: IDependencyField[] = [];
        res?.forEach(
          (el: {
            valueOnValueShortInfo: IDependencyField[];
            propertyOnValue: number[];
          }) => {
            values.push(...el.valueOnValueShortInfo);
            el?.propertyOnValue?.forEach((item) => {
              if (
                !propertyOnValueResult.includes(item) &&
                (!props.availableProperties?.length ||
                  props.availableProperties?.find((e) => e.id === item))
              )
                propertyOnValueResult.push(item);
            });
            el?.valueOnValueShortInfo?.forEach((item) => {
              if (
                !result.includes(item.customPropertyId) &&
                (!props.availableProperties?.length ||
                  props.availableProperties?.find(
                    (e) => e.id === item.customPropertyId
                  ))
              )
                result.push(item.customPropertyId);
            });
          }
        );
        return [result, values, propertyOnValueResult];
      });
    for (let i = 0; i < dependenciesIds[0].length; i++) {
      const dependencyValue = await getCustomPropertiesById(
        dependenciesIds[0][i],
        {
          params: { OccupationId: occupationsId },
        }
      );
      if (dependencyValue?.headers?.success) {
        const item = dependencyValue.data;
        const values = dependenciesIds
          ?.at(1)
          ?.filter(
            (e) => typeof e === "object" && e.customPropertyId === item.id
          ) as IDependencyField[];

        const convertedValues = values?.map((e: IDependencyField) =>
          convertSelectDefaultValue(
            e,
            item.id,
            e.customPropertyCode,
            i18n.language
          )
        );
        const getDefaultValue = () => {
          const defaultValue = get(
            item,
            "defaultProfileListValue.listValueId",
            0
          );
          const foundedValue = convertedValues?.find(
            (e) => e?.value === defaultValue
          );
          if (foundedValue) {
            return foundedValue;
          } else return convertedValues?.at(0);
        };

        const newItem = {
          ...item,
          values: convertedValues,
          parentKey: String(value.key),
          value: getDefaultValue()?.value,
          name: item[`clientName${capitalizeFirstLetter(i18n.language)}`],
          isSelect: item.isList,
          size: item.fieldSize,
          required: item.isRequired,
          regexp: item.regularExpression,
          regexpText:
            item[
              "regularExpressionError" + capitalizeFirstLetter(i18n.language)
            ],
          defaultValue: getDefaultValue(),
        };

        const index = newCP.findIndex(
          (e) => e.id === newItem.id || e.id === newItem.customPropertyId
        );

        if (index !== -1) {
          newCP.splice(index, 1);
        }
        newCP.push(newItem);
      }
    }

    for (let i = 0; i < dependenciesIds[2].length; i++) {
      const dependencyValue = await getCustomPropertiesById(
        dependenciesIds[2][i],
        {
          params: { OccupationId: occupationsId },
        }
      );
      if (dependencyValue?.headers?.success) {
        const item = dependencyValue.data;

        const newItem = {
          ...item,
          parentKey: String(value.key),
          name: item[`clientName${capitalizeFirstLetter(i18n.language)}`],
          isSelect: item.isList,
          size: item.fieldSize,
          required: item.isRequired,
          regexp: item.regularExpression,
          regexpText:
            item[
              "regularExpressionError" + capitalizeFirstLetter(i18n.language)
            ],
          defaultValue: get(item, "defaultProfileListValue.listValueId", 0),
        };

        if (item?.isList) {
          const values = await postCustomPropertiesListValues({
            CustomPropertyId: item?.id,
            Object: {
              LogicalName: getObjectLogicalName(props),
              ...props.dataExpense,
            },
            ObjectCustomProperties: {
              [`${item.id || 0}`]: {
                Value: item.name || "",
                ListValueId: item.id,
              },
            },
          });

          const convertedValues = values?.data?.map((e: any) =>
            convertSelectOptionData(
              {
                listValueId: e.id,
                nameRu: e.nameRu,
                nameEn: e.nameEn,
              },
              e.customPropertyId,
              e.customPropertyCode,
              i18n.language
            )
          );

          const getDefaultValue = () => {
            const defaultValue = get(
              item,
              "defaultProfileListValue.listValueId",
              0
            );
            const foundedValue = convertedValues?.find(
              (e: any) => e?.value === defaultValue
            );
            if (foundedValue) {
              return foundedValue;
            } else return convertedValues?.at(0);
          };
          newItem.defaultValue = getDefaultValue();
          newItem.values = convertedValues;
          newItem.value = getDefaultValue()?.value;
        }

        const index = newCP.findIndex(
          (e) => e.id === newItem.id || e.id === newItem.customPropertyId
        );

        if (index !== -1) {
          newCP.splice(index, 1);
        }
        newCP.push(newItem);
      }
    }

    if (!newCP.length) {
      dispatch(loaderUnlock());
      setLoading(false);
      return;
    }

    let result = customPropertyFieldMapper(
      newCP.sort((a, b) => a.ordinal - b.ordinal)
    );

    if (props.isEdit) {
      if (idObject) {
        const params = result.map((item) => item.codeName);
        if (params.length > 0) {
          const request = await getCustomPropertiesCodes({
            params: {
              ObjectLogicalNameId: idObject,
              ExternalObjectId: props.id,
              CustomPropertyCodes: params,
            },
            paramsSerializer: (params: any) =>
              qs.stringify(params, { arrayFormat: "repeat" }),
          });

          if (request?.headers?.success) {
            setCodes((prev) => [...prev, ...request?.data]);
            result.forEach((item) => {
              if (item.codeName) {
                const code: CPCode[] = request.data.filter(
                  (e: CPCode) => e.customPropertyCode === item.codeName
                );
                const defaultValue = code.flatMap(
                  (e) =>
                    convertSelectOptionData(
                      {
                        listValueId: e.listValueId,
                        nameRu: e.nameRu,
                        nameEn: e.nameEn,
                        percent: e.isAllocation ? e.percent || 100 : undefined,
                      },
                      String(item.id),
                      item.codeName,
                      i18n.language,
                      item?.key,
                      value?.key || item?.parentKey
                    ) || []
                );
                if (code?.length === 1 && !item.isAllocationAvailable) {
                  if (defaultValue[0]) {
                    item.defaultValue = defaultValue[0];
                    if (defaultValue?.at(0)?.value) {
                      item.value = String(defaultValue?.at(0)?.value);
                    } else if (defaultValue?.at(0)?.text) {
                      item.value = String(defaultValue?.at(0)?.text);
                    }
                  }
                  if (code[0].id) {
                    item.editId = code[0].id;
                  }
                } else if (item.isAllocationAvailable) {
                  if (defaultValue?.length) {
                    item.defaultValue = defaultValue;
                    item.value = defaultValue;
                  }
                } else if ("key" in value) {
                  const foundCode = code.find(
                    (e) => String(e.parentId) === item.parentKey
                  );
                  if (foundCode) {
                    const newValue = convertSelectOptionData(
                      {
                        listValueId: foundCode.listValueId,
                        nameRu: foundCode.nameRu,
                        nameEn: foundCode.nameEn,
                        percent: foundCode.percent,
                      },
                      String(item.id),
                      item.codeName,
                      i18n.language,
                      item?.key,
                      value?.key || item?.parentKey
                    );
                    if (newValue) {
                      item.defaultValue = newValue;
                      item.value = String(newValue.value);
                      item.editId = foundCode.id;
                    }
                  }
                }
              }
            });
          }
        }
      } else {
        result.forEach((item) => {
          const code = codes.find(
            (e: CPCode) =>
              String(e.customPropertyId) === String(item.customPropertyId)
          );
          if (code && !item.editId) {
            item.editId = code.id;
          }
        });
      }
    }

    for (let i = 0; i < result.length; i++) {
      const item = result[i];
      const existingField = fields?.find((e) => e.key === item.key);
      if (existingField) {
        update(
          fields.findIndex((i) => i.key === item.key),
          { ...item }
        );
      } else {
        append(item);
      }
    }

    for (let i = 0; i < result.length; i++) {
      const item = result[i];
      if (item.isSelect && !item.isAllocationAvailable) {
        await handleSelectValueChange(
          { ...item.defaultValue, key: item.key, parentKey: item.parentKey },
          idObject
        );
      } else if (item.isAllocationAvailable) {
        await handleChangeAllocation(
          item.defaultValue as SelectOptionType[],
          idObject
        );
      }
    }
    dispatch(loaderUnlock());
    setLoading(false);
  };

  const getData = async () => {
    dispatch(loaderLock());
    reset({ customFields: [] });
    setLoading(true);
    let result: ICustomProperties[] = [];
    let idObject: number;
    await getObjectLogicalId(props, setObjectLogicalNameId)
      .then((id) => {
        idObject = id;
        setObjectLogicalNameId(id);
        const getCP = async () =>
          await getCustomProperties({
            params: {
              CompanyId: companyId,
              ObjectLogicalNameId: id,
              EmployeeID: userDetail.id,
              OccupationID: occupationsId,
            },
          });

        return getCP();
      })
      .then(async (cPRequest) => {
        const filtered = !!props.availableProperties?.length
          ? cPRequest.data.filter((item: any) => {
              return props.availableProperties?.find((e) => e.id === item.id);
            })
          : cPRequest.data;
        if (cPRequest?.headers.success) {
          for (let i = 0; i < filtered.length; i++) {
            const item = filtered[i];
            const defaultValue = convertSelectOptionData(
              {
                listValueId: get(
                  item,
                  "defaultProfileListValue.listValueId",
                  0
                ),
                nameRu: get(item, "defaultProfileListValue.nameRu", ""),
                nameEn: get(item, "defaultProfileListValue.nameEn", ""),
                percent: get(
                  item,
                  "defaultProfileListValue.percent",
                  item?.isAllocationAvailable ? 100 : undefined
                ),
              },
              item.id,
              item.codeName,
              i18n.language
            );
            const newItem = {
              ...item,
              name: item[`clientName${capitalizeFirstLetter(i18n.language)}`],
              isSelect: item.isList,
              size: item.fieldSize,
              required: item.isRequired,
              regexp: item.regularExpression,
              regexpText:
                item[
                  "regularExpressionError" +
                    capitalizeFirstLetter(i18n.language)
                ],
              defaultValue:
                item.isAllocationAvailable && defaultValue
                  ? [defaultValue]
                  : !item.isAllocationAvailable
                    ? defaultValue
                    : null,
              value: item?.isAllocationAvailable
                ? defaultValue
                : defaultValue?.value,
            };
            if (newItem.isList) {
              const values = await postCustomPropertiesListValues({
                CustomPropertyId: newItem.id,
                Object: {
                  LogicalName: getObjectLogicalName(props),
                  ...props.dataExpense,
                },
                ObjectCustomProperties: {
                  [`${newItem.id || 0}`]: {
                    Value: newItem.name || "",
                    ListValueId: newItem.id,
                  },
                },
              });

              if (values.headers.success) {
                if (!values.data.length) {
                  const errorMessage = `По списковому ДД отсутствуют возможные значения`;
                  console.error("---error:", errorMessage);
                  dispatch(
                    showErrors({
                      code: "",
                      message: errorMessage,
                    })
                  );
                  return;
                }
                const convertedValues = values?.data?.map((e: any) =>
                  convertSelectOptionData(
                    {
                      listValueId: e.id,
                      nameRu: e.nameRu,
                      nameEn: e.nameEn,
                    },
                    e.customPropertyId,
                    e.customPropertyCode,
                    i18n.language
                  )
                );
                newItem["values"] = convertedValues;
              }

              if (
                (newItem["defaultValue"]?.value === 0 ||
                  !newItem["defaultValue"]) &&
                !newItem.isAllocationAvailable
              ) {
                newItem["defaultValue"] = newItem["values"][0];
                newItem["value"] = newItem["values"][0].value;
              }
              if (newItem.isAllocationAvailable && !newItem["defaultValue"]) {
                newItem["defaultValue"] = [
                  { ...newItem["values"][0], percent: 100 },
                ];
                newItem["value"] = [{ ...newItem["values"][0], percent: 100 }];
              }
            }
            result.push(newItem);
          }

          if (props.isEdit) {
            const params = filtered.map((item: any) => item.codeName);
            if (params.length > 0) {
              const request = await getCustomPropertiesCodes({
                params: {
                  ObjectLogicalNameId: idObject,
                  ExternalObjectId: props.id,
                  CustomPropertyCodes: params,
                },
                paramsSerializer: (params: any) =>
                  qs.stringify(params, { arrayFormat: "repeat" }),
              });

              if (request?.headers?.success) {
                setCodes((prev) => [...prev, ...request?.data]);
                result.forEach((item) => {
                  if (item.codeName) {
                    const code: CPCode[] = request.data.filter(
                      (e: CPCode) => e.customPropertyCode === item.codeName
                    );
                    const defaultValue = code.flatMap((e) => {
                      return (
                        convertSelectOptionData(
                          {
                            listValueId: e.listValueId,
                            nameRu: e.nameRu,
                            nameEn: e.nameEn,
                            percent: item.isAllocationAvailable
                              ? e.percent || 100
                              : undefined,
                          },
                          String(item.id),
                          item.codeName,
                          i18n.language,
                          String(e.id)
                        ) || []
                      );
                    });
                    if (code?.length === 1 && !item.isAllocationAvailable) {
                      if (defaultValue[0]) {
                        item.defaultValue = defaultValue[0];
                        if (defaultValue?.at(0)?.value) {
                          item.value = String(defaultValue?.at(0)?.value);
                        } else if (defaultValue?.at(0)?.text) {
                          item.value = String(defaultValue?.at(0)?.text);
                        }
                      }
                      if (code[0].id) {
                        item.editId = code[0].id;
                      }
                    } else if (item.isAllocationAvailable) {
                      if (defaultValue?.length) {
                        item.defaultValue = defaultValue;
                        item.value = defaultValue;
                      }
                    }
                  }
                });
              }
            }
          }
        } else {
          dispatch(
            showErrors({
              code: "",
              message: "Не удалось получить доп. данные",
            })
          );
        }
        result = result.sort((a, b) => a.ordinal - b.ordinal);
        const mappedResult = customPropertyFieldMapper(result);
        for (let i = 0; i < mappedResult.length; i++) {
          const item = mappedResult[i];
          const existingField = fields?.find((e) => e.key === item.key);
          const defaultValue =
            item.isAllocationAvailable && Array.isArray(item.defaultValue)
              ? item.defaultValue?.map((e, index) => ({
                  ...e,
                  key: !!e?.key ? e?.key : `${item.key}-${index + 1}`,
                }))
              : { ...item.defaultValue, key: item.key };
          if (existingField) {
            update(
              fields.findIndex((i) => i.id === item.id),
              { ...item, defaultValue: defaultValue }
            );
          } else {
            append({
              ...item,
              defaultValue: defaultValue,
            });
          }
          if (!item.isAllocationAvailable && item.isSelect) {
            await handleSelectValueChange(defaultValue, String(idObject));
          } else if (item.isAllocationAvailable) {
            await handleChangeAllocation(
              defaultValue as SelectOptionType[],
              String(idObject)
            );
          }
        }
      });
    dispatch(loaderUnlock());
    setLoading(false);
  };

  useEffect(() => {
    if (
      ((props.isAO || props.isZNR) &&
        props.availableProperties !== null &&
        props.businessGoal &&
        props.companyId) ||
      (!props.isAO && !props.isZNR)
    ) {
      if (!isLoading) {
        reset({});
        getData();
      }
    } else {
      setLoading(false);
      reset({ customFields: [] });
    }
  }, [props.availableProperties, props.dataExpense?.consumptionType?.value]);

  if (isLoading || (!props.businessGoal && (props.isAO || props.isZNR))) {
    return <></>;
  }

  return (
    <>
      <div className="box-modal-form-block custom-property">
        <form style={{ width: "100%" }}>
          {!!fields?.length &&
            fields?.map((f: ICustomField) => (
              <Controller
                key={f.key}
                name={f.name}
                control={control as any}
                defaultValue={f.defaultValue}
                render={({ field: { value, onChange } }) => {
                  return (
                    <CustomField
                      props={props}
                      item={f}
                      isError={errors && !!errors["customFields"]?.message}
                      value={value}
                      onChange={(
                        value,
                        _,
                        percentChanged,
                        deleteKey,
                        isAppend,
                        newSelectValue
                      ) => {
                        onChange(value);
                        f.isAllocationAvailable
                          ? handleChangeAllocation(
                              value,
                              undefined,
                              percentChanged,
                              deleteKey,
                              isAppend,
                              newSelectValue
                            )
                          : handleSelectValueChange(value, undefined);
                      }}
                    />
                  );
                }}
              />
            ))}
        </form>
      </div>
    </>
  );
};

export default memo(CustomProperty);
